import { ActivityIndicator, ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, ScreenContainer, Switch } from "@src/components";
import {
  NotificationPreferencesForm,
  NotificationAccessBanner,
  type NotificationChannel,
} from "@visioni/a3s-notifications/app";
import { useNotificationPreferencesPage } from "@src/features/settings/hooks";
import { usePushNotificationSwitch } from "@src/features/notifications";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

/** Push without the feature flag: no switch to register this device, so no column to opt in with either. */
const PUSH_HIDDEN: ReadonlySet<NotificationChannel> = new Set(["push"]);

export default function NotificationPreferencesScreen() {
  const { theme } = useUnistyles();
  const {
    isNotificationSettingsEnabled,
    isLoading,
    isError,
    allCameras,
    sites,
    cameras,
    onToggleAllCamerasChannel,
    onToggleSiteChannel,
    onToggleCameraChannel,
    phoneNumber,
    onSavePhoneNumber,
    accessNoticeReason,
  } = useNotificationPreferencesPage();
  const isPushEnabled = useBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);
  const pushSwitch = usePushNotificationSwitch({ enabled: isNotificationSettingsEnabled && isPushEnabled });

  if (!isNotificationSettingsEnabled) {
    return null;
  }

  return (
    <ScreenContainer edges={["bottom"]}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: theme.spacing["5xl"], paddingTop: theme.spacing["4xl"] }}
      >
        {isLoading ?
          <View py="4xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text as string} />
          </View>
        : isError ?
          <View py="4xl" alignItems="center">
            <Text textStyle="sm/regular" color="textMuted">
              {"Couldn't load your notification preferences. Please try again."}
            </Text>
          </View>
        : <NotificationPreferencesForm
            allCameras={allCameras}
            onToggleAllCamerasChannel={onToggleAllCamerasChannel}
            sites={sites}
            onToggleSiteChannel={onToggleSiteChannel}
            cameras={cameras}
            onToggleCameraChannel={onToggleCameraChannel}
            phoneNumber={phoneNumber}
            onSavePhoneNumber={onSavePhoneNumber}
            accessNotice={accessNoticeReason ? <NotificationAccessBanner reason={accessNoticeReason} /> : null}
            pushSwitch={
              isPushEnabled ?
                {
                  ...pushSwitch,
                  renderSwitch: ({ active, onToggle, disabled }) => (
                    <Switch active={active} onToggle={onToggle} disabled={disabled} size="medium" />
                  ),
                }
              : undefined
            }
            hiddenChannels={isPushEnabled ? undefined : PUSH_HIDDEN}
          />
        }
      </ScrollView>
    </ScreenContainer>
  );
}
